import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdClose, MdDownload } from 'react-icons/md';
import ImageViewerModal from './ImageViewerModal';
import ImageViewerCapacityModal from './ImageViewerCapacityModal';
import { useImageViewers } from './ImageViewerContext';
import './multiImageViewer.scss';

export const IMAGE_VIEWER_WORKSPACE_SPLIT_EVENT = 'image-viewer:workspace-split';

export type ImageViewerWorkspaceSplitDetail = {
  readonly open: boolean;
  readonly percent: number;
};

export type MultiImagePlaybackPanel = {
  readonly layerCount: number;
  readonly title?: string;
  readonly onStop: () => void;
  readonly children: React.ReactNode;
  readonly onDownloadAll?: () => void;
  readonly canDownloadAll?: boolean;
  readonly showDownloadAll?: boolean;
};

const MIN_SPLIT_PERCENT = 35;
const MAX_SPLIT_PERCENT = 75;

export function resolveSplitPercentForCount(count: number): number {
  if (count <= 0) return 70;
  if (count <= 2) return 70; // ~30% image pane
  if (count === 3) return 62; // ~38%
  if (count === 4) return 55; // ~45%
  if (count <= 6) return 48; // ~52%
  if (count <= 9) return 42; // ~58%
  return 38; // ~62%
}

function resolveGridClass(count: number): string {
  if (count <= 1) return 'multi-image-grid--1';
  if (count === 2) return 'multi-image-grid--2';
  if (count === 3) return 'multi-image-grid--3';
  if (count === 4) return 'multi-image-grid--4';
  if (count <= 6) return 'multi-image-grid--6';
  if (count <= 9) return 'multi-image-grid--9';
  return 'multi-image-grid--many';
}

function publishWorkspaceSplit(open: boolean, percent: number): void {
  window.dispatchEvent(
    new CustomEvent<ImageViewerWorkspaceSplitDetail>(IMAGE_VIEWER_WORKSPACE_SPLIT_EVENT, {
      detail: { open, percent },
    }),
  );
}

type Props = {
  readonly playback?: MultiImagePlaybackPanel | null;
};

/**
 * Split-screen workspace for survey images (map left, images right).
 */
const MultiImageViewer: React.FC<Props> = ({ playback = null }) => {
  const { viewers, closeViewer, capacityMessage, clearCapacityMessage } =
    useImageViewers();
  const [splitPercent, setSplitPercent] = useState(() => resolveSplitPercentForCount(1));
  const [isDraggingSplit, setIsDraggingSplit] = useState(false);
  const splitPercentRef = useRef(splitPercent);
  splitPercentRef.current = splitPercent;
  const prevCountRef = useRef(0);
  const wasOpenRef = useRef(false);

  const isPlayback = playback != null;
  const count = isPlayback ? playback.layerCount : viewers.length;
  const isOpen = isPlayback || viewers.length > 0;
  const capacityModal = (
    <ImageViewerCapacityModal
      isOpen={Boolean(capacityMessage)}
      message={capacityMessage ?? ''}
      onClose={clearCapacityMessage}
    />
  );

  // Grow/shrink the image pane from slot count 
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      prevCountRef.current = 0;
      return;
    }
    if (isDraggingSplit) {
      prevCountRef.current = count;
      return;
    }

    const target = resolveSplitPercentForCount(count);
    const prevCount = prevCountRef.current;
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;

    if (justOpened || count < prevCount || prevCount === 0) {
      setSplitPercent(target);
    } else if (count > prevCount) {
      // Accumulating: only widen the image pane (never shrink after a manual drag).
      setSplitPercent((current) => Math.min(current, target));
    }

    prevCountRef.current = count;
  }, [count, isDraggingSplit, isOpen]);

  useEffect(() => {
    publishWorkspaceSplit(isOpen, splitPercent);
    return () => {
      publishWorkspaceSplit(false, splitPercentRef.current);
    };
  }, [isOpen, splitPercent]);

  useEffect(() => {
    if (!isDraggingSplit) return;

    const handleMove = (ev: MouseEvent) => {
      const pct = (ev.clientX / window.innerWidth) * 100;
      setSplitPercent(Math.min(MAX_SPLIT_PERCENT, Math.max(MIN_SPLIT_PERCENT, pct)));
    };
    const handleUp = () => setIsDraggingSplit(false);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDraggingSplit]);

  const handleCloseAll = useCallback(() => {
    if (isPlayback) {
      playback.onStop();
      return;
    }
    clearCapacityMessage();
    viewers.forEach((v) => closeViewer(v.id));
  }, [clearCapacityMessage, closeViewer, isPlayback, playback, viewers]);

  const handleDownloadAllClickViewers = useCallback(() => {
    const buttons = document.querySelectorAll<HTMLButtonElement>(
      '.multi-image-slot .image-viewer-download-btn',
    );
    buttons.forEach((button) => {
      if (!button.disabled) {
        button.click();
      }
    });
  }, []);

  const showDownloadAll = isPlayback
    ? (playback.showDownloadAll ?? playback.layerCount > 1)
    : viewers.length > 1;
  const canDownloadAll = isPlayback
    ? (playback.canDownloadAll ?? false)
    : true;
  const handleDownloadAll = isPlayback
    ? playback.onDownloadAll
    : handleDownloadAllClickViewers;

  if (!isOpen) {
    return capacityModal;
  }

  const title = isPlayback
    ? (playback.title ?? `Survey playback (${playback.layerCount})`)
    : viewers.length > 0
      ? `Survey images (${viewers.length})`
      : 'Survey images';

  return (
    <>
      {capacityModal}
      <div
        className={`multi-image-splitter${isDraggingSplit ? ' is-dragging' : ''}`}
        style={{ left: `${splitPercent}vw` }}
        onMouseDown={() => setIsDraggingSplit(true)}
      />
      <div
        className="multi-image-split-container"
        style={{ left: `${splitPercent}vw` }}
        role="dialog"
        aria-label={isPlayback ? 'Survey playback' : 'Survey image viewer'}
      >
        <div className="multi-image-header">
          <span>{title}</span>
          <div className="multi-image-controls">
            {showDownloadAll && handleDownloadAll && (
              <button
                type="button"
                className="multi-image-control-btn multi-image-control-btn--with-label"
                onClick={handleDownloadAll}
                disabled={!canDownloadAll}
                title={
                  canDownloadAll
                    ? 'Download all visible images'
                    : 'No visible images are ready to download'
                }
              >
                <MdDownload />
                <span>Download all</span>
              </button>
            )}
            <div
              className="modal-close"
              onClick={handleCloseAll}
              title={isPlayback ? 'Stop playback' : 'Close'}
              role="button"
              aria-label={isPlayback ? 'Stop playback' : 'Close'}
            >
              <MdClose />
            </div>
          </div>
        </div>

        <div className="multi-image-body">
          {isPlayback ? (
            playback.children
          ) : (
            <div className={`multi-image-grid ${resolveGridClass(viewers.length)}`}>
              {viewers.map((viewer) => (
                <div
                  key={viewer.id}
                  className={`multi-image-slot${viewer.isActive ? ' multi-image-slot--active' : ''}`}
                >
                  <ImageViewerModal viewer={viewer} layout="slot" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MultiImageViewer;
